import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { functionsBaseUrl } from '@/config/functionsUrl';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { showToast } from '@/components/ui/Toast';
import {
  ArrowLeft, Car, Gauge, Calendar, Wrench, Shield,
  Edit2, Save, X, Plus, Loader2, Sparkles, AlertTriangle,
} from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAiAction } from '@/hooks/useAiAction';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface VehicleInsurance {
  provider?: string;
  policyNumber?: string;
  expiryDate?: string;
  phone?: string;
}

export interface ExtendedVehicle {
  id: string;
  name: string;
  householdId: string;
  type?: string;
  fuelType: string;
  make?: string;
  model?: string;
  year?: number;
  licensePlate?: string;
  vin?: string;
  color?: string;
  currentMileage?: number;
  nextServiceMileage?: number;
  nextServiceDate?: string;
  purchaseDate?: string;
  insurance?: VehicleInsurance;
  notes?: string;
}

interface MileageEntry {
  id: string;
  date: string;
  mileage: number;
  notes?: string;
}

interface ServiceRecord {
  id: string;
  date: string;
  mileage?: number;
  service: string;
  cost?: string;
  provider?: string;
  notes?: string;
}

interface Booking {
  id: string;
  vehicleId: string;
  start: string;
  end: string;
  bookedBy: string;
  notes?: string;
}

// ── API helper ─────────────────────────────────────────────────────────────────

async function apiFetch(path: string, method: string, body?: object): Promise<any> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsBaseUrl}/apiVehicles${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error ?? 'Request failed');
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Constants ──────────────────────────────────────────────────────────────────

export const FUEL_LABELS: Record<string, string> = {
  gas: '⛽ Gas', diesel: '⛽ Diesel', electric: '⚡ Electric', hybrid: '🔋 Hybrid',
};

const FUEL_TYPES = ['gas', 'diesel', 'electric', 'hybrid'];
const VEHICLE_TYPES = ['Sedan', 'SUV', 'Truck', 'Van', 'Motorcycle', 'Other'];

// ── Sub-components ─────────────────────────────────────────────────────────────

const Field: React.FC<{
  label: string;
  value?: string | number;
}> = ({ label, value }) => (
  <div>
    <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">{label}</p>
    <p className="text-sm font-semibold text-slate-900 dark:text-white">
      {value ?? <span className="text-slate-400 italic font-normal">Not set</span>}
    </p>
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  editing?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  saving?: boolean;
  accentClass?: string;
  children: React.ReactNode;
}> = ({ title, icon, editing, onEdit, onSave, onCancel, saving, accentClass = '', children }) => (
  <div className={`bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 space-y-4 ${accentClass}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">{title}</h3>
      </div>
      {editing ? (
        <div className="flex gap-2">
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
          <button onClick={onCancel} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </div>
      ) : onEdit ? (
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-brand text-2xs font-black uppercase tracking-widest hover:bg-brand/5 rounded-xl transition-colors"
        >
          <Edit2 className="w-3 h-3" /> Edit
        </button>
      ) : null}
    </div>
    {children}
  </div>
);

// ── VehicleProfile ─────────────────────────────────────────────────────────────

interface VehicleProfileProps {
  vehicle: ExtendedVehicle;
  householdId: string;
  onBack: () => void;
  onUpdate: (updated: ExtendedVehicle) => void;
}

export const VehicleProfile: React.FC<VehicleProfileProps> = ({ vehicle, householdId, onBack, onUpdate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [local, setLocal] = useState<ExtendedVehicle>(vehicle);

  // Overview editing
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailDraft, setDetailDraft] = useState<ExtendedVehicle>({ ...vehicle });
  const [savingDetails, setSavingDetails] = useState(false);

  // Insurance editing
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceDraft, setInsuranceDraft] = useState<VehicleInsurance>({ ...(vehicle.insurance ?? {}) });
  const [savingInsurance, setSavingInsurance] = useState(false);

  // Mileage
  const [mileageLog, setMileageLog] = useState<MileageEntry[]>([]);
  const [mileageLoading, setMileageLoading] = useState(false);
  const [showAddMileage, setShowAddMileage] = useState(false);
  const [newMileage, setNewMileage] = useState('');
  const [newMileageNotes, setNewMileageNotes] = useState('');
  const [savingMileage, setSavingMileage] = useState(false);

  // Service
  const [serviceRecords, setServiceRecords] = useState<ServiceRecord[]>([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [serviceDraft, setServiceDraft] = useState({ service: '', date: '', mileage: '', cost: '', provider: '', notes: '' });
  const [savingService, setSavingService] = useState(false);

  // Bookings
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [bookingDraft, setBookingDraft] = useState({ start: '', end: '', notes: '' });
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // AI service prediction
  const { callAction: callAi } = useAiAction(householdId);
  const [aiResults, setAiResults] = useState<Array<{ service: string; urgency: string; estimatedMileage?: number }> | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const persist = async (fields: Partial<ExtendedVehicle>) => {
    await updateDoc(doc(db, 'households', householdId, 'vehicles', vehicle.id), fields as Record<string, any>);
    const updated = { ...local, ...fields };
    setLocal(updated);
    onUpdate(updated);
  };

  const loadMileage = useCallback(async () => {
    setMileageLoading(true);
    try {
      const res = await apiFetch(`/${vehicle.id}/mileage?householdId=${householdId}`, 'GET');
      setMileageLog(res.data ?? []);
    } catch {} finally { setMileageLoading(false); }
  }, [vehicle.id, householdId]);

  const loadService = useCallback(async () => {
    setServiceLoading(true);
    try {
      const res = await apiFetch(`/${vehicle.id}/service?householdId=${householdId}`, 'GET');
      setServiceRecords(res.data ?? []);
    } catch {} finally { setServiceLoading(false); }
  }, [vehicle.id, householdId]);

  const loadBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const res = await apiFetch(`/${vehicle.id}/bookings?householdId=${householdId}`, 'GET');
      setBookings(res.data ?? []);
    } catch {} finally { setBookingsLoading(false); }
  }, [vehicle.id, householdId]);

  useEffect(() => {
    if (activeTab === 'mileage') loadMileage();
    if (activeTab === 'service') loadService();
    if (activeTab === 'bookings') loadBookings();
  }, [activeTab, loadMileage, loadService, loadBookings]);

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      await persist({
        name: detailDraft.name,
        make: detailDraft.make,
        model: detailDraft.model,
        year: detailDraft.year,
        fuelType: detailDraft.fuelType,
        type: detailDraft.type,
        color: detailDraft.color,
        vin: detailDraft.vin,
        licensePlate: detailDraft.licensePlate,
        currentMileage: detailDraft.currentMileage,
        nextServiceMileage: detailDraft.nextServiceMileage,
        nextServiceDate: detailDraft.nextServiceDate,
        purchaseDate: detailDraft.purchaseDate,
        notes: detailDraft.notes,
      });
      setEditingDetails(false);
    } catch (e: any) { showToast(e.message, 'error'); } finally { setSavingDetails(false); }
  };

  const handleSaveInsurance = async () => {
    setSavingInsurance(true);
    try {
      await persist({ insurance: insuranceDraft });
      setEditingInsurance(false);
    } catch (e: any) { showToast(e.message, 'error'); } finally { setSavingInsurance(false); }
  };

  const handleLogMileage = async () => {
    if (!newMileage) return;
    setSavingMileage(true);
    try {
      await apiFetch(`/${vehicle.id}/mileage`, 'POST', {
        householdId,
        mileage: parseInt(newMileage),
        date: new Date().toISOString().split('T')[0],
        notes: newMileageNotes || undefined,
      });
      await persist({ currentMileage: parseInt(newMileage) });
      await loadMileage();
      setShowAddMileage(false);
      setNewMileage('');
      setNewMileageNotes('');
    } catch (e: any) { showToast(e.message, 'error'); } finally { setSavingMileage(false); }
  };

  const handleAddService = async () => {
    if (!serviceDraft.service || !serviceDraft.date) return;
    setSavingService(true);
    try {
      const res = await apiFetch(`/${vehicle.id}/service`, 'POST', {
        householdId,
        service: serviceDraft.service,
        date: serviceDraft.date,
        mileage: serviceDraft.mileage ? parseInt(serviceDraft.mileage) : undefined,
        cost: serviceDraft.cost || undefined,
        provider: serviceDraft.provider || undefined,
        notes: serviceDraft.notes || undefined,
      });
      setServiceRecords(prev => [res.data, ...prev]);
      setShowAddService(false);
      setServiceDraft({ service: '', date: '', mileage: '', cost: '', provider: '', notes: '' });
    } catch (e: any) { showToast(e.message, 'error'); } finally { setSavingService(false); }
  };

  const handleAddBooking = async () => {
    if (!bookingDraft.start || !bookingDraft.end) return;
    setSavingBooking(true);
    setBookingError(null);
    try {
      const res = await apiFetch(`/${vehicle.id}/bookings`, 'POST', {
        householdId,
        start: bookingDraft.start,
        end: bookingDraft.end,
        notes: bookingDraft.notes || undefined,
      });
      setBookings(prev => [...prev, res.data]);
      setShowAddBooking(false);
      setBookingDraft({ start: '', end: '', notes: '' });
    } catch (e: any) { setBookingError(e.message); } finally { setSavingBooking(false); }
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await apiFetch(`/${vehicle.id}/bookings/${bookingId}?householdId=${householdId}`, 'DELETE');
      setBookings(prev => prev.filter(b => b.id !== bookingId));
    } catch (e: any) { showToast(e.message, 'error'); }
  };

  const handleAiService = async () => {
    setAiLoading(true);
    try {
      const result = await callAi<{ recommendations: Array<{ service: string; urgency: string; estimatedMileage?: number }> }>(
        'predictVehicleService',
        { vehicleName: local.name, make: local.make, model: local.model, year: local.year, currentMileage: local.currentMileage }
      );
      if (result) setAiResults(result.recommendations);
    } finally { setAiLoading(false); }
  };

  const tabs = [
    { id: 'overview', label: t('vehicles.tab_overview', 'Overview') },
    { id: 'mileage', label: t('vehicles.tab_mileage', 'Mileage') },
    { id: 'service', label: t('vehicles.tab_service', 'Service') },
    { id: 'bookings', label: t('vehicles.tab_bookings', 'Bookings') },
    { id: 'insurance', label: t('vehicles.tab_insurance', 'Insurance') },
  ];

  const inputCls = 'w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40';

  return (
    <div className="max-w-page-full mx-auto p-4 md:p-6 space-y-6 pb-32 animate-in fade-in duration-500">

      {/* Header */}
      <header className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black tracking-tighter text-slate-900 dark:text-white truncate">{local.name}</h1>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em] mt-0.5">
            {[local.year, local.make, local.model].filter(Boolean).join(' ') || t('vehicles.vehicle_profile', 'Vehicle Profile')}
          </p>
        </div>
        <span className="flex-shrink-0 text-2xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
          {FUEL_LABELS[local.fuelType] ?? local.fuelType}
        </span>
      </header>

      {/* Tabs */}
      <TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} variant="underline" />

      {/* ── Overview ──────────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <SectionCard
            title="Vehicle Details"
            icon={<Car className="w-4 h-4 text-brand" />}
            editing={editingDetails}
            onEdit={() => { setDetailDraft({ ...local }); setEditingDetails(true); }}
            onSave={handleSaveDetails}
            onCancel={() => setEditingDetails(false)}
            saving={savingDetails}
          >
            {editingDetails ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Name *</p>
                  <input className={inputCls} value={detailDraft.name} onChange={e => setDetailDraft(d => ({ ...d, name: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Make</p>
                  <input className={inputCls} value={detailDraft.make ?? ''} onChange={e => setDetailDraft(d => ({ ...d, make: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Model</p>
                  <input className={inputCls} value={detailDraft.model ?? ''} onChange={e => setDetailDraft(d => ({ ...d, model: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Year</p>
                  <input className={inputCls} type="number" value={detailDraft.year ?? ''} onChange={e => setDetailDraft(d => ({ ...d, year: parseInt(e.target.value) || undefined }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Color</p>
                  <input className={inputCls} value={detailDraft.color ?? ''} onChange={e => setDetailDraft(d => ({ ...d, color: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">License Plate</p>
                  <input className={inputCls} value={detailDraft.licensePlate ?? ''} onChange={e => setDetailDraft(d => ({ ...d, licensePlate: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">VIN</p>
                  <input className={inputCls} value={detailDraft.vin ?? ''} onChange={e => setDetailDraft(d => ({ ...d, vin: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Fuel Type</p>
                  <select className={inputCls} value={detailDraft.fuelType} onChange={e => setDetailDraft(d => ({ ...d, fuelType: e.target.value }))}>
                    {FUEL_TYPES.map(f => <option key={f} value={f}>{FUEL_LABELS[f] ?? f}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Vehicle Type</p>
                  <select className={inputCls} value={detailDraft.type ?? ''} onChange={e => setDetailDraft(d => ({ ...d, type: e.target.value }))}>
                    <option value="">Select type</option>
                    {VEHICLE_TYPES.map(t => <option key={t} value={t.toLowerCase()}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Current Mileage</p>
                  <input className={inputCls} type="number" value={detailDraft.currentMileage ?? ''} onChange={e => setDetailDraft(d => ({ ...d, currentMileage: parseInt(e.target.value) || undefined }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Purchase Date</p>
                  <input className={inputCls} type="date" value={detailDraft.purchaseDate ?? ''} onChange={e => setDetailDraft(d => ({ ...d, purchaseDate: e.target.value }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Next Service (mileage)</p>
                  <input className={inputCls} type="number" value={detailDraft.nextServiceMileage ?? ''} onChange={e => setDetailDraft(d => ({ ...d, nextServiceMileage: parseInt(e.target.value) || undefined }))} />
                </div>
                <div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Next Service (date)</p>
                  <input className={inputCls} type="date" value={detailDraft.nextServiceDate ?? ''} onChange={e => setDetailDraft(d => ({ ...d, nextServiceDate: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Notes</p>
                  <textarea className={inputCls} rows={3} value={detailDraft.notes ?? ''} onChange={e => setDetailDraft(d => ({ ...d, notes: e.target.value }))} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label="Make" value={local.make} />
                <Field label="Model" value={local.model} />
                <Field label="Year" value={local.year} />
                <Field label="Color" value={local.color} />
                <Field label="License Plate" value={local.licensePlate} />
                <Field label="VIN" value={local.vin} />
                <Field label="Fuel Type" value={FUEL_LABELS[local.fuelType] ?? local.fuelType} />
                <Field label="Vehicle Type" value={local.type} />
                <Field label="Current Mileage" value={local.currentMileage ? `${local.currentMileage.toLocaleString()} mi` : undefined} />
                <Field label="Purchase Date" value={local.purchaseDate} />
                {local.notes && (
                  <div className="col-span-2 sm:col-span-3">
                    <Field label="Notes" value={local.notes} />
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {/* Next service due banner */}
          {(local.nextServiceMileage || local.nextServiceDate) && (
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-[2rem] p-5 flex items-start gap-4">
              <Wrench className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">{t('vehicles.next_service_due', 'Next Service Due')}</p>
                <div className="flex gap-4 mt-1.5">
                  {local.nextServiceMileage && (
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{local.nextServiceMileage.toLocaleString()} mi</p>
                  )}
                  {local.nextServiceDate && (
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{local.nextServiceDate}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Mileage ───────────────────────────────────────────────────────────── */}
      {activeTab === 'mileage' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddMileage(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {t('vehicles.log_mileage', 'Log Mileage')}
            </button>
          </div>

          {mileageLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          ) : mileageLog.length === 0 ? (
            <EmptyState
              icon={<Gauge className="w-8 h-8 text-slate-400" />}
              title={t('vehicles.no_mileage_title', 'No mileage entries')}
              description={t('vehicles.no_mileage_desc', 'Start logging mileage to track vehicle usage over time.')}
            />
          ) : (
            <div className="space-y-2">
              {mileageLog.map(entry => (
                <div key={entry.id} className="flex items-center justify-between bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{entry.mileage.toLocaleString()} mi</p>
                    {entry.notes && <p className="text-xs text-slate-500 mt-0.5">{entry.notes}</p>}
                  </div>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400">{entry.date}</p>
                </div>
              ))}
            </div>
          )}

          {showAddMileage && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddMileage(false)} />
              <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-4 mx-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">{t('vehicles.log_mileage', 'Log Mileage')}</h2>
                  <button onClick={() => setShowAddMileage(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
                <input
                  className={inputCls} type="number"
                  placeholder="Current odometer reading *"
                  value={newMileage}
                  onChange={e => setNewMileage(e.target.value)}
                />
                <input
                  className={inputCls}
                  placeholder="Notes (optional)"
                  value={newMileageNotes}
                  onChange={e => setNewMileageNotes(e.target.value)}
                />
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowAddMileage(false)} className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                  <button onClick={handleLogMileage} disabled={!newMileage || savingMileage} className="flex-1 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors">
                    {savingMileage ? t('common.saving', 'Saving...') : t('vehicles.log', 'Log')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Service ───────────────────────────────────────────────────────────── */}
      {activeTab === 'service' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleAiService}
              disabled={aiLoading}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand/5 text-brand text-xs font-black uppercase tracking-widest rounded-xl hover:bg-brand/10 disabled:opacity-60 transition-colors border border-brand/20"
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              AI: Predict Service
            </button>
            <button
              onClick={() => setShowAddService(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Record
            </button>
          </div>

          {aiResults && (
            <div className="bg-white dark:bg-slate-900 border border-brand/20 rounded-[2rem] p-5 space-y-3">
              <p className="text-2xs font-black uppercase tracking-widest text-brand">{t('vehicles.ai_recommendations', 'AI Recommendations')}</p>
              {aiResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{r.service}</span>
                  <div className="text-right">
                    <span className={`text-2xs font-black uppercase ${r.urgency === 'high' ? 'text-rose-500' : r.urgency === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`}>
                      {r.urgency}
                    </span>
                    {r.estimatedMileage && (
                      <p className="text-[10px] text-slate-400 mt-0.5">~{r.estimatedMileage.toLocaleString()} mi</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {serviceLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          ) : serviceRecords.length === 0 ? (
            <EmptyState
              icon={<Wrench className="w-8 h-8 text-slate-400" />}
              title={t('vehicles.no_service_title', 'No service records')}
              description={t('vehicles.no_service_desc', 'Log maintenance to keep a full history of your vehicle.')}
            />
          ) : (
            <div className="space-y-3">
              {serviceRecords.map(r => (
                <div key={r.id} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-900 dark:text-white">{r.service}</p>
                      {r.provider && <p className="text-xs text-slate-500 mt-0.5">{r.provider}</p>}
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-2xs font-black uppercase tracking-widest text-slate-400">{r.date}</p>
                      {r.cost && <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">{r.cost}</p>}
                    </div>
                  </div>
                  {(r.mileage || r.notes) && (
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex gap-4">
                      {r.mileage && <span className="text-xs text-slate-400">{r.mileage.toLocaleString()} mi</span>}
                      {r.notes && <span className="text-xs text-slate-400">{r.notes}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAddService && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddService(false)} />
              <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-md shadow-2xl space-y-4 mx-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">{t('vehicles.add_service_record', 'Add Service Record')}</h2>
                  <button onClick={() => setShowAddService(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
                <input className={inputCls} placeholder="Service type *" value={serviceDraft.service} onChange={e => setServiceDraft(d => ({ ...d, service: e.target.value }))} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Date *</p>
                    <input className={inputCls} type="date" value={serviceDraft.date} onChange={e => setServiceDraft(d => ({ ...d, date: e.target.value }))} />
                  </div>
                  <input className={inputCls} type="number" placeholder="Mileage" value={serviceDraft.mileage} onChange={e => setServiceDraft(d => ({ ...d, mileage: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input className={inputCls} placeholder="Cost (e.g. $120)" value={serviceDraft.cost} onChange={e => setServiceDraft(d => ({ ...d, cost: e.target.value }))} />
                  <input className={inputCls} placeholder="Provider" value={serviceDraft.provider} onChange={e => setServiceDraft(d => ({ ...d, provider: e.target.value }))} />
                </div>
                <input className={inputCls} placeholder="Notes (optional)" value={serviceDraft.notes} onChange={e => setServiceDraft(d => ({ ...d, notes: e.target.value }))} />
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowAddService(false)} className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                  <button onClick={handleAddService} disabled={!serviceDraft.service || !serviceDraft.date || savingService} className="flex-1 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors">
                    {savingService ? t('common.saving', 'Saving...') : t('vehicles.add_record', 'Add Record')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Bookings ──────────────────────────────────────────────────────────── */}
      {activeTab === 'bookings' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => { setShowAddBooking(true); setBookingError(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {t('vehicles.reserve', 'Reserve')}
            </button>
          </div>

          {bookingsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          ) : bookings.length === 0 ? (
            <EmptyState
              icon={<Calendar className="w-8 h-8 text-slate-400" />}
              title={t('vehicles.no_bookings_title', 'No bookings')}
              description={t('vehicles.no_bookings_desc', 'Reserve this vehicle for specific time slots to avoid conflicts.')}
            />
          ) : (
            <div className="space-y-3">
              {bookings.map(b => (
                <div key={b.id} className="flex items-start justify-between bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{b.start} → {b.end}</p>
                    {b.notes && <p className="text-xs text-slate-500 mt-1">{b.notes}</p>}
                  </div>
                  <button
                    onClick={() => handleCancelBooking(b.id)}
                    className="p-1.5 text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors flex-shrink-0 ml-3"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddBooking && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddBooking(false)} />
              <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-4 mx-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">{t('vehicles.reserve_vehicle', 'Reserve Vehicle')}</h2>
                  <button onClick={() => setShowAddBooking(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
                {bookingError && (
                  <div className="flex items-center gap-2 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-3 text-rose-600 dark:text-rose-400 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />{bookingError}
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400">Start</p>
                  <input className={inputCls} type="datetime-local" value={bookingDraft.start} onChange={e => setBookingDraft(d => ({ ...d, start: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400">End</p>
                  <input className={inputCls} type="datetime-local" value={bookingDraft.end} onChange={e => setBookingDraft(d => ({ ...d, end: e.target.value }))} />
                </div>
                <input className={inputCls} placeholder="Notes (optional)" value={bookingDraft.notes} onChange={e => setBookingDraft(d => ({ ...d, notes: e.target.value }))} />
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowAddBooking(false)} className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                  <button onClick={handleAddBooking} disabled={!bookingDraft.start || !bookingDraft.end || savingBooking} className="flex-1 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors">
                    {savingBooking ? t('common.saving', 'Saving...') : t('vehicles.reserve', 'Reserve')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Insurance ─────────────────────────────────────────────────────────── */}
      {activeTab === 'insurance' && (
        <SectionCard
          title="Insurance"
          icon={<Shield className="w-4 h-4 text-brand" />}
          editing={editingInsurance}
          onEdit={() => { setInsuranceDraft({ ...(local.insurance ?? {}) }); setEditingInsurance(true); }}
          onSave={handleSaveInsurance}
          onCancel={() => setEditingInsurance(false)}
          saving={savingInsurance}
        >
          {!local.insurance?.provider && !editingInsurance ? (
            <EmptyState
              icon={<Shield className="w-8 h-8 text-slate-400" />}
              title={t('vehicles.no_insurance_title', 'No insurance on file')}
              description={t('vehicles.no_insurance_desc', 'Add your insurance details for quick reference.')}
            >
              <button
                onClick={() => { setInsuranceDraft({}); setEditingInsurance(true); }}
                className="px-5 py-2.5 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors"
              >
                {t('vehicles.add_insurance', 'Add Insurance')}
              </button>
            </EmptyState>
          ) : editingInsurance ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Provider</p>
                <input className={inputCls} value={insuranceDraft.provider ?? ''} onChange={e => setInsuranceDraft(d => ({ ...d, provider: e.target.value }))} />
              </div>
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Policy Number</p>
                <input className={inputCls} value={insuranceDraft.policyNumber ?? ''} onChange={e => setInsuranceDraft(d => ({ ...d, policyNumber: e.target.value }))} />
              </div>
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Expiry Date</p>
                <input className={inputCls} type="date" value={insuranceDraft.expiryDate ?? ''} onChange={e => setInsuranceDraft(d => ({ ...d, expiryDate: e.target.value }))} />
              </div>
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Phone</p>
                <input className={inputCls} type="tel" value={insuranceDraft.phone ?? ''} onChange={e => setInsuranceDraft(d => ({ ...d, phone: e.target.value }))} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Provider" value={local.insurance?.provider} />
              <Field label="Policy Number" value={local.insurance?.policyNumber} />
              <Field label="Expiry Date" value={local.insurance?.expiryDate} />
              <Field label="Phone" value={local.insurance?.phone} />
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};
